import React from "react";
import { Box, Paper, Stack, Typography, Divider } from "@mui/material";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function ReturnPolicy() {
  const { style } = useStyle();

  return (
    <>
      <Seo
        title="Return Policy | Parish Audio"
        description="Read the Parish Audio return policy for digital downloads, plugin purchases, subscriptions, and refund eligibility."
        canonical="https://parishaudio.com/return-policy"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Return Policy | Parish Audio",
          url: "https://parishaudio.com/return-policy",
          description:
            "Parish Audio return policy covering digital products, subscriptions, and refund requests.",
        }}
      />

      <Box sx={{ width: "95%", maxWidth: 950, mx: "auto", my: 4 }}>
        <Paper
          sx={{
            p: { xs: 2, md: 4 },
            backgroundColor: style.color1,
            color: style.color4,
            borderRadius: 3,
            boxShadow: 10,
          }}
        >
          <Stack spacing={3}>
            <Box>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", md: "2.6rem" },
                  fontWeight: 800,
                  mb: 1,
                  color: style.color4,
                }}
              >
                Return Policy
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                Last updated: March 14, 2026
              </Typography>
            </Box>

            <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
              At Parish Audio, we want you to have a great experience with our
              products. Because our products are digital downloads and software
              licenses, returns work differently than they do for physical goods.
              Please review the policy below before making a purchase.
            </Typography>

            <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography
                variant="h2"
                sx={{ fontSize: "1.35rem", fontWeight: 700, mb: 1, color: style.color4 }}
              >
                Digital Products
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                Parish Audio products are sold as digital software downloads.
                Because digital products are delivered instantly and cannot be
                physically returned, all sales are generally considered final once
                the product has been purchased and delivered.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{ fontSize: "1.35rem", fontWeight: 700, mb: 1, color: style.color4 }}
              >
                Refund Eligibility
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                Refund requests may be considered on a case-by-case basis in
                limited circumstances, including:
              </Typography>

              <Box
                component="ul"
                sx={{
                  pl: 3,
                  color: style.color3,
                  "& li": { mb: 1, lineHeight: 1.7 },
                }}
              >
                <li>You were charged more than once for the same product by mistake.</li>
                <li>You purchased the same product multiple times accidentally.</li>
                <li>The product cannot be downloaded or activated due to a verified technical issue that we are unable to resolve.</li>
                <li>A refund is otherwise required by applicable law.</li>
              </Box>

              <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                Refunds are not guaranteed for reasons such as change of mind,
                incompatibility with unsupported systems, misunderstanding of product
                features, or failure to review product requirements before purchase.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{ fontSize: "1.35rem", fontWeight: 700, mb: 1, color: style.color4 }}
              >
                Trial and Subscription Purchases
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                If a product or service is offered through a free trial or
                subscription, you are responsible for canceling before the next
                billing date if you do not wish to continue. Subscription charges
                that have already been processed are generally non-refundable unless
                required by law or approved by Parish Audio at our discretion.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{ fontSize: "1.35rem", fontWeight: 700, mb: 1, color: style.color4 }}
              >
                Compatibility and System Requirements
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                Please review the listed system requirements, supported plugin
                formats, operating systems, and host compatibility before
                purchasing. We are happy to help answer questions before you buy.
                Refund requests based on unsupported systems or unsupported DAWs
                may be denied if the compatibility information was already provided.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{ fontSize: "1.35rem", fontWeight: 700, mb: 1, color: style.color4 }}
              >
                Technical Support
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                If you experience any problem downloading, installing, activating,
                or using one of our products, please contact us first. In many
                cases, technical issues can be resolved quickly without the need
                for a refund.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{ fontSize: "1.35rem", fontWeight: 700, mb: 1, color: style.color4 }}
              >
                How to Request a Refund
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                To request a refund, please contact us at{" "}
                <a href="mailto:support@parishaudio.com">support@parishaudio.com</a>{" "}
                with:
              </Typography>

              <Box
                component="ul"
                sx={{
                  pl: 3,
                  color: style.color3,
                  "& li": { mb: 1, lineHeight: 1.7 },
                }}
              >
                <li>Your full name</li>
                <li>The email address used for purchase</li>
                <li>Your order number or transaction details</li>
                <li>A brief description of the issue</li>
              </Box>

              <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                We will review your request and respond as soon as reasonably
                possible.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{ fontSize: "1.35rem", fontWeight: 700, mb: 1, color: style.color4 }}
              >
                Chargebacks
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                If you have a billing problem, please contact us before filing a
                chargeback. Filing a chargeback without contacting us first may
                result in suspension or termination of access to your purchased
                products, licenses, or account while the dispute is being reviewed.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{ fontSize: "1.35rem", fontWeight: 700, mb: 1, color: style.color4 }}
              >
                Contact
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                If you have any questions about this Return Policy, please contact:
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.8, mt: 1 }}>
                Parish Audio
                <br />
                Email: <a href="mailto:support@parishaudio.com">support@parishaudio.com</a>
                <br />
                Website: <a href="https://parishaudio.com">https://parishaudio.com</a>
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </>
  );
}